// hooks/useChat.js
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { conversation } from "network/ApiAxios";
import { useChatContext } from 'contexts/ChatContext';
import config from 'core/config';

export const useChat = (id) => {
    const navigate = useNavigate();
    const { conversationsUpdated, refreshConversations } = useChatContext();
    const token = localStorage.getItem('token');

    // State
    const [messages, setMessages] = useState([]);
    const [userPrompt, setUserPrompt] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [regenerateMessageId, setRegenerateMessageId] = useState(null);
    const [loadingStartTime, setLoadingStartTime] = useState(null);
    const [animatingMessage, setAnimatingMessage] = useState('');
    const [animatingIndex, setAnimatingIndex] = useState(0);

    // Refs
    const scrollRef = useRef(null);
    const formRef = useRef(null);
    const textareaRef = useRef(null);
    const requestRef = useRef(null);
    const animationSpeed = 15;

    // Auto-resize textarea
    const adjustTextareaHeight = () => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = 'auto';
            textarea.style.height = `${textarea.scrollHeight}px`;
            textarea.style.overflowY = textarea.scrollHeight > 200 ? 'auto' : 'hidden';
        }
    };

    // Load conversation
    useEffect(() => {
        if (id) {
            const getConversation = async () => {
                try {
                    const data = await conversation(token, id);
                    setMessages(data.mapping ? Object.values(data.mapping) : []);
                } catch (error) {
                    console.error('Failed to fetch conversation:', error);
                    setMessages([]);
                }

                if (scrollRef.current) {
                    scrollRef.current.scrollIntoView({ behavior: 'smooth' });
                }
            };
            getConversation();
        }
    }, [id, token, regenerateMessageId, conversationsUpdated]);

    // Auto-scroll
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages]);

    // Animation effect
    useEffect(() => {
        if (animatingMessage && animatingIndex < animatingMessage.length) {
            requestRef.current = setTimeout(() => {
                setMessages(prevMessages => {
                    const newMessages = [...prevMessages];
                    const lastMessageIndex = newMessages.length - 1;

                    if (lastMessageIndex < 0) return newMessages;

                    const currentText = animatingMessage.substring(0, animatingIndex + 1);
                    newMessages[lastMessageIndex] = {
                        ...newMessages[lastMessageIndex],
                        message: {
                            ...newMessages[lastMessageIndex].message,
                            content: {
                                ...newMessages[lastMessageIndex].message.content,
                                parts: [currentText]
                            }
                        }
                    };
                    return newMessages;
                });
                setAnimatingIndex(prevIndex => prevIndex + 1);
            }, animationSpeed);
        } else if (animatingMessage) {
            setAnimatingMessage('');
            setAnimatingIndex(0);
        }

        return () => clearTimeout(requestRef.current);
    }, [animatingMessage, animatingIndex, animationSpeed]);

    // Create user message
    const createUserMessage = (queryText) => {
        const timestamp = new Date().getTime();
        const id = `user-${timestamp}`;

        return {
            id,
            message: {
                id,
                author: { role: "user" },
                create_time: new Date().toISOString(),
                update_time: new Date().toISOString(),
                content: {
                    content_type: "text",
                    parts: [queryText]
                },
            },
            parent: null,
            children: []
        };
    };

    // Type writer effect
    const typeWriter = (assistantMessageId, newText) => {
        setAnimatingMessage(newText);
        setAnimatingIndex(0);
    };

    // Parse streaming data
    const parseStreamLine = (line) => {
        if (!line || line === '[DONE]') return null;

        try {
            return JSON.parse(line);
        } catch (error) {
            console.warn("Failed to parse JSON line:", error.message);
            console.debug("Problematic line:", line.substring(0, 200) + "...");

            if (line.includes('"status":"DONE"')) {
                const match = line.match(/"conversation_id"\s*:\s*"([^"]+)"/);
                if (match && match[1]) {
                    return { status: 'DONE', conversation_id: match[1] };
                }
            }

            return null;
        }
    };

    // Handle message submission
    const handleSubmit = async (event, query = null, messageId = null, parentId = null) => {
        if (event && event.preventDefault) {
            event.preventDefault();
        }

        query = query ?? userPrompt.trim();
        if (!query) return;

        setIsLoading(true);
        setLoadingStartTime(Date.now());
        setUserPrompt('');

        const messageInput = formRef.current?.elements.messageInput;
        if (!messageInput) {
            console.error('Message input not found');
            setIsLoading(false);
            return;
        }

        const messageValue = query || messageInput.value;
        const userMessage = createUserMessage(userPrompt);

        if (messageId) {
            setRegenerateMessageId(messageId);
        } else {
            setMessages(prevMessages => {
                const newMessages = [...prevMessages, userMessage];

                // Immediately add empty assistant message to show loading
                const assistantMessage = {
                    id: `assistant-${Date.now()}`,
                    message: {
                        id: `assistant-${Date.now()}`,
                        author: { role: "assistant" },
                        create_time: new Date().toISOString(),
                        update_time: new Date().toISOString(),
                        content: {
                            content_type: "text",
                            parts: [""]
                        },
                    },
                    parent: null,
                    children: []
                };

                return [...newMessages, assistantMessage];
            });
        }

        try {
            const response = await fetch(config.API_BASE_URL + 'v1/conversation/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${token}`
                },
                body: JSON.stringify({
                    message: messageValue,
                    parent_id: parentId || null,
                    session_id: id || null,
                    message_id: messageId || null
                })
            });

            const reader = response.body.getReader();
            const decoder = new TextDecoder();

            let buffer = '';
            let done = false;
            let assistantMessage = {
                id: messageId || '',
                message: {
                    id: messageId || '',
                    author: { role: "assistant" },
                    create_time: new Date().toISOString(),
                    update_time: new Date().toISOString(),
                    content: {
                        content_type: "text",
                        parts: [""]
                    },
                },
                parent: null,
                children: []
            };

            // For regeneration, add the assistant message; for new messages, it's already added
            if (messageId) {
                setMessages(prevMessages => [...prevMessages, assistantMessage]);
            }

            let fullMessage = '';
            let conversationId = null;

            while (!done) {
                const { value, done: streamDone } = await reader.read();
                done = streamDone;

                if (value) {
                    const chunk = decoder.decode(value, { stream: true });
                    buffer += chunk;

                    const allLines = buffer.split('\n');
                    const completeLines = [];

                    for (let i = 0; i < allLines.length - 1; i++) {
                        const line = allLines[i].trim();
                        if (line.startsWith('data:')) {
                            completeLines.push(line.replace(/^data:\s*/, '').trim());
                        }
                    }

                    const lastLine = allLines[allLines.length - 1];
                    buffer = lastLine || '';

                    const lines = completeLines;

                    for (const line of lines) {
                        try {
                            const parsed = parseStreamLine(line);

                            if (!parsed) continue;

                            if (parsed.status === 'DONE') {
                                done = true;
                                conversationId = parsed.conversation_id;
                                continue;
                            }

                            if (parsed.id) {
                                assistantMessage.id = parsed.id;
                                assistantMessage.message.id = parsed.id;
                            }

                            if (parsed.parent) {
                                assistantMessage.parent = parsed.parent;
                            }

                            const parts = parsed.message?.content?.parts;
                            if (parts && parts.length > 0) {
                                fullMessage = parts[0];
                                typeWriter(assistantMessage.id, fullMessage);
                            }
                        } catch (lineError) {
                            console.error('Error processing stream line:', lineError);
                            console.debug('Problematic line:', line);
                            continue;
                        }
                    }
                }
            }

            if (conversationId) {
                navigate(`/c/${conversationId}`);
            }

        } catch (error) {
            console.error('Failed to send message:', error);
        } finally {
            const minLoadingTime = 800;
            const elapsedTime = loadingStartTime ? Date.now() - loadingStartTime : 0;
            const remainingTime = Math.max(0, minLoadingTime - elapsedTime);

            setTimeout(() => {
                setIsLoading(false);
                setLoadingStartTime(null);
                setRegenerateMessageId(null);
                refreshConversations();
            }, remainingTime);
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSubmit(event);
        }
    };

    return {
        // State
        messages,
        userPrompt,
        isLoading,
        regenerateMessageId,

        // Setters
        setUserPrompt,

        // Refs
        scrollRef,
        formRef,
        textareaRef,

        // Functions
        handleSubmit,
        handleKeyDown,
        adjustTextareaHeight
    };
};